import { inventoryTypeUIDs } from "@/global/config/constants";
import { TeamConfig } from "@/global/config/teamConfigs/types";
import {
  GameFilters,
  InventoryType,
  SeatLevelInventory,
  InventoryPerk,
  SuitLevelV2Inventory,
  GroupLevelInventory,
} from "@/global/store/data/types";

function validateSeatLevelEligibility(inventories: InventoryType[]) {
  let result = {
    isEventValidated: false,
    isInventoriesValidated: false,
  };
  if (inventories && inventories?.length > 0) {
    result.isEventValidated = true;
    result.isInventoriesValidated = true;
  }
  return result;
}

function validateSectionLevelEligibility(inventories: InventoryType[]) {
  let result = {
    isEventValidated: false,
    isInventoriesValidated: false,
  };
  if (inventories && inventories?.length > 0) {
    result.isEventValidated = true;
    result.isInventoriesValidated = true;
  }
  return result;
}

function validateSuiteLevelEligibility(inventories: InventoryType[]) {
  let result = {
    isEventValidated: false,
    isInventoriesValidated: false,
  };
  if (inventories && inventories?.length > 0) {
    result.isEventValidated = true;
    result.isInventoriesValidated = true;
  }
  return result;
}

function validateGroupLevelEligibility(inventories: InventoryType[]) {
  let result = {
    isEventValidated: false,
    isInventoriesValidated: false,
  };
  if (inventories && inventories?.length > 0) {
    result.isEventValidated = true;
    result.isInventoriesValidated = true;
  }
  return result;
}

function validateAdaEligibility(
  inventories: InventoryType[],
  seatCount: number
) {
  let result = {
    isEventValidated: false,
    isInventoriesValidated: false,
  };
  let isWheelChairAdded = inventories?.some((itm) => {
    if (itm.inventoryType == "ADA") {
      return itm.seatType == "WHEELCHAIR";
    }
  });
  let isAllCompanion = inventories?.every((itm) => {
    if (itm.inventoryType == "ADA") {
      return itm.seatType == "COMPANION";
    }
  });

  let isAllLimitedMobility = inventories?.every((itm) => {
    if (itm.inventoryType == "ADA") {
      return itm.seatType == "LIMITED MOBILITY";
    }
  });

  if (inventories.length >= seatCount && isAllCompanion) {
    result.isEventValidated = false;
    result.isInventoriesValidated = true;
    return result;
  }

  if (inventories.length >= seatCount && isWheelChairAdded) {
    result.isInventoriesValidated = true;
    result.isEventValidated = true;
    return result;
  }

  if (inventories.length >= seatCount && isAllLimitedMobility) {
    result.isInventoriesValidated = true;
    result.isEventValidated = true;
    return result;
  }

  return result;
}

export function validateInventorySelection(
  inventories: InventoryType[],
  filters: Partial<GameFilters> & { inventoryType: string }
) {
  let result = { isEventValidated: false, isInventoriesValidated: false };
  let { inventoryType } = filters;

  if (
    (inventoryType === inventoryTypeUIDs.suitLevel ||
      inventoryType == inventoryTypeUIDs.suiteLevelV2) &&
    inventories?.length
  ) {
    result = validateSuiteLevelEligibility(inventories);
  }

  if (inventoryType === inventoryTypeUIDs.seatLevel && inventories?.length) {
    result = validateSeatLevelEligibility(inventories);
  }

  if (inventoryType === inventoryTypeUIDs.seatLevelMulti && inventories?.length) {
    result = validateSeatLevelEligibility(inventories);
  }

  if (inventoryType === inventoryTypeUIDs.sectionLevel && inventories?.length) {
    result = validateSectionLevelEligibility(inventories);
  }

  if (inventoryType === inventoryTypeUIDs.groupLevel && inventories?.length) {
    result = validateGroupLevelEligibility(inventories);
  }

  if (inventoryType === inventoryTypeUIDs.ada && inventories?.length) {
    const seatCount = filters?.seatCount as number;
    result = validateAdaEligibility(inventories, seatCount);
  }

  return result;
}

/*
 +------------------------------------------------------------------------------+
 * [ util to create SEAT-LEVEL inventory card title ]
 +------------------------------------------------------------------------------+ */
// prettier-ignore
export function createSeatLevelCardTitle(inv: any, teamConfig: TeamConfig) {
  const { formatUiSectionName } = teamConfig.seatMapConfigs;
  const { firstSeatNumber, lastSeatNumber, sectionName, rowName, inventoryType, displaySectionName } = inv as SeatLevelInventory;

  const formattedSectionName = formatUiSectionName?.(sectionName) || displaySectionName || sectionName; 
  const seatNumbers = firstSeatNumber === lastSeatNumber ? `${firstSeatNumber}` : `${firstSeatNumber}-${lastSeatNumber}`;
  const seatLabel = firstSeatNumber === lastSeatNumber ? "Seat" : "Seats";

  return inventoryType === "ADA" ? 
    `Section ${formattedSectionName}, Row ${rowName}, <br /> Seat ${inv?.seatNumber}` : 
    `Section ${formattedSectionName}, Row ${rowName}, <br /> ${seatLabel} ${seatNumbers}`;
}

// function to calculate total price of tickets plus perks
export function calculateTicketPlusPerksPrice(
  inventory: (InventoryType & SuitLevelV2Inventory) | GroupLevelInventory
): number {
  const perks = inventory?.perks || [];
  const totalAllInPrice = inventory?.totalAllInPrice;
  const totalPerksPrice = perks.reduce(
    (acc: number, perk: any) =>
      acc +
      (Number(perk.basePrice) + Number(perk.serviceFee)) *
        Number(perk.seatCount),
    0
  );
  return totalPerksPrice + totalAllInPrice;
}

export function calculateSuiteName(
  suiteName: string,
  teamConfig: TeamConfig
): string {
  if (teamConfig?.seatMapConfigs?.suiteSectionNameMapping) {
    const mapping = teamConfig.seatMapConfigs.suiteSectionNameMapping;
    for (const [customName, suiteNames] of Object.entries(mapping)) {
      if (suiteNames.includes(suiteName)) return customName;
    }
  }
  return suiteName;
}
